

import _ from 'lodash';
import Joi from 'joi';
import PropTypes from 'prop-types';

import Form from '../../../common/form';

import { getFileUploadTypes, matchPhrases } from '../../../../utils/utilities';
import { PEC_SCAN_LOCATIONS } from '../../../../utils/constants';
import { CoverDataItem } from '../../../../utils/models';

import toastService from '../../../../services/toastService';

class AUTScanDataItemEditForm extends Form {
	state = {
		data: {
			zoneName: '',
			scanLocation: '',
			limitation: '',
			scanImage: [],
			scanArea: [],
			minimumThickness: '',
			nominalThickness: '',
			corrosionAllowance: '',
			axialLength: '',
			circLength: '',
			axialLoc: '',
			circLoc: '',
		},
		errors: {},
		loading: false,
		progress: 0,
		scanLocationOptions: PEC_SCAN_LOCATIONS,
	};

	static propTypes = {
		initialData: PropTypes.object,
		onEdit: PropTypes.func,
	};

	schema = {
		zoneName: Joi.string().min(3).required().label('Zone Name'),
		scanLocation: Joi.object({
			label: Joi.string().required(),
			value: Joi.string().required(),
			__isNew__: Joi.boolean().optional(),
		}).required().label('Scan Location'),
		limitation: Joi.string().min(3).required().label('Limitation'),
		scanImage: Joi.array().min(1).required().label('Scan Image'),
		scanArea: Joi.array().min(1).required().label('Scan Area'),
		minimumThickness: Joi.alternatives().try(Joi.number().positive(), Joi.string().valid('NG')).required().label('Minimum Thickness'),
		nominalThickness: Joi.alternatives().try(Joi.number().positive(), Joi.string().valid('NG')).required().label('Nominal Thickness'),
		corrosionAllowance: Joi.alternatives().try(Joi.number().positive(), Joi.string().valid('NG')).required().label('Corrosion Allowance'),
		axialLength: Joi.number().positive().required().label('Axial Length'),
		circLength: Joi.number().positive().required().label('Circ. Length'),
		axialLoc: Joi.number().positive().required().label('Axial. Loc.'),
		circLoc: Joi.number().positive().required().label('Circ. Loc.'),
	};

	componentDidMount() {
		if (!this.props.initialData) {
			throw new Error('No AUT Scan data found!');
		};

		const scanLocation = { label: this.props.initialData.scanLocation,  value: this.props.initialData.scanLocation };
		const initialData: any = { ...this.props.initialData, scanLocation };

		const locationMatch = PEC_SCAN_LOCATIONS.find((scanLocation) => matchPhrases(scanLocation.value, this.props.initialData.scanLocation));
		const scanLocationOptions: CoverDataItem[] = locationMatch ? PEC_SCAN_LOCATIONS : [...PEC_SCAN_LOCATIONS, scanLocation];

		this.setState({ data: initialData, scanLocationOptions } as any);
	}

	getData = () => {
		const finalData = _.omit(this.state.data, ['scanLocation']);

		return {
			...finalData,
			scanLocation: (this.state.data.scanLocation as unknown as CoverDataItem).value,
		}
	};

	doSubmit = (): void => {
		this.props.onEdit(this.getData());
		toastService.success('Updated Successfully!');
	};

	handleNewOptionCreation = (newOption: string) => {
		const newMultiSelectOption = this.createNewMultiSelectOption(newOption);

		const scanLocationOptions = [...this.state.scanLocationOptions, newMultiSelectOption];
		const data = { ...this.state.data, scanLocation: newMultiSelectOption } as any;

		this.setState({ data, scanLocationOptions } as any);
	};

	render() {
		return (
			<div className="p-4 rounded bg-light bordered mb-4">
				<form autoComplete="off" onSubmit={this.handleSubmit}>
					<h2 className="form-title">Scan Data Overview</h2>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'zoneName',
								placeholder: 'Enter Zone Name',
								label: 'Zone Name',
							})}
						</div>

						<div className="col">
							{this.renderCreatableMultiselect({
								closeMenuOnSelect: true,
								options: this.state.scanLocationOptions,
								name: 'scanLocation',
								placeholder: 'Enter Scan Location',
								label: 'Scan Location',
								isMulti: false,
								onCreateOption: this.handleNewOptionCreation
							})}
						</div>

						<div className="col">
							{this.renderTextArea({
								rows: 5,
								name: 'limitation',
								placeholder: 'Enter Limitation',
								label: 'Limitation',
							})}
						</div>

						<div className="col">
							{this.renderFileInput({
								max: 1,
								multiple: false,
								name: 'scanImage',
								accept: 'image/*,application/pdf',
								label: 'Scan Image',
								sublabel: 'Upload Scan Image',
								types: getFileUploadTypes(),
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'minimumThickness',
								label: 'Minimum Thickness',
								placeholder: 'Enter Minimum Thickness',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'nominalThickness',
								label: 'Nominal Thickness',
								placeholder: 'Enter Nominal Thickness',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'corrosionAllowance',
								label: 'Corrosion Allowance',
								placeholder: 'Enter Corrosion Allowance',
							})}
						</div>

						<div className="col">
							{this.renderFileInput({
								max: 1,
								multiple: false,
								name: 'scanArea',
								accept: 'image/*,application/pdf',
								label: 'Scan Area',
								sublabel: 'Upload Scan Area',
								types: getFileUploadTypes(),
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>
					
					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'number',
								name: 'axialLength',
								label: 'Axial Length',
								placeholder: 'Enter Axial Length',
							})}
						</div>
						
						<div className="col">
							{this.renderInput({
								type: 'number',
								name: 'circLength',
								label: 'Circ Length',
								placeholder: 'Enter Circ Length',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'number',
								name: 'axialLoc',
								label: 'Axial Loc.',
								placeholder: 'Enter Axial Loc.',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'number',
								name: 'circLoc',
								label: 'Circ. Loc.',
								placeholder: 'Enter Circ. Loc.',
							})}
						</div>
					</div>

					<div className="mt-4 my-2 divider"></div>

					<div className="d-flex justify-content-end">
						{this.renderButton('Update Scan Details')}
					</div>
				</form>
			</div>
		);
	}
}

export default AUTScanDataItemEditForm;